import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  Send,
  Forum,
  Favorite,
  FavoriteBorder,
  DeleteOutline,
  Person,
  Logout,
  AccessTime
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Google Multi-Color SVG Icon
const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

// Formatador de tempo relativo em português
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'Agora há pouco';
  try {
    const now = new Date();
    const date = new Date(dateStr);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (isNaN(diffInSeconds) || diffInSeconds < 45) return 'Agora há pouco';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `Há ${diffInMinutes} min`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Há ${diffInHours} h`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
    return date.toLocaleDateString('pt-BR');
  } catch (e) {
    return 'Agora há pouco';
  }
};

// Normalizador universal de comentários para compatibilidade total de campos
const normalizeComment = (c, fallbackUser = null) => {
  if (!c) return null;
  const id = c.id || c._id || (c.createdAt ? String(c.createdAt) : String(Math.random()));
  const userName =
    c.userName ||
    c.user_name ||
    c.name ||
    c.author ||
    (fallbackUser?.id && (c.googleId === fallbackUser.id || c.google_id === fallbackUser.id) ? fallbackUser.name : '') ||
    'Ouvinte NostalBeat';
  const userPicture =
    c.userPicture ||
    c.user_picture ||
    c.picture ||
    (fallbackUser?.id && (c.googleId === fallbackUser.id || c.google_id === fallbackUser.id) ? fallbackUser.picture : '') ||
    '';
  const googleId = c.googleId || c.google_id || c.userId || '';
  const likesCount = c.likesCount ?? c.likes_count ?? c.likes ?? 0;
  const createdAt = c.createdAt || c.created_at || c.time || new Date().toISOString();

  return {
    ...c,
    id,
    _id: id,
    userName,
    user_name: userName,
    userPicture,
    user_picture: userPicture,
    googleId,
    google_id: googleId,
    likesCount,
    likes_count: likesCount,
    createdAt,
    created_at: createdAt,
  };
};

const GoogleCommentsSection = () => {
  const googleBtnRef = useRef(null);
  const commentsListRef = useRef(null);

  // Lista de comentários vindos da API
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Mapa de curtidas do usuário no navegador local
  const [likedMap, setLikedMap] = useState(() => {
    try {
      const saved = localStorage.getItem('nostalbeat_liked_comments');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Usuário autenticado com o Google
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('nostalbeat_google_user');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (parsed && parsed.provider === 'google' && parsed.id) {
        return parsed;
      }
      return null;
    } catch (e) {
      return null;
    }
  });

  const [message, setMessage] = useState('');

  // Buscar comentários da API do backend
  const fetchComments = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const response = await api.get('/comments');
      const rawList =
        response.data?.comments ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      if (Array.isArray(rawList)) {
        setComments(rawList.map((c) => normalizeComment(c, user)).filter(Boolean));
      }
    } catch (err) {
      console.warn('Mural: Não foi possível carregar comentários da API:', err.message);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [user]);

  // Carregar comentários ao montar e manter sincronizado a cada 20 segundos
  useEffect(() => {
    fetchComments();
    const pollInterval = setInterval(() => {
      fetchComments(true);
    }, 20000);

    return () => clearInterval(pollInterval);
  }, [fetchComments]);

  // Decodificar JWT do Google Identity Services
  const handleCredentialResponse = (response) => {
    try {
      const base64Url = response.credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const data = JSON.parse(jsonPayload);

      const loggedUser = {
        id: data.sub,
        name: data.name,
        email: data.email,
        picture: data.picture,
        provider: 'google',
      };

      setUser(loggedUser);
      localStorage.setItem('nostalbeat_google_user', JSON.stringify(loggedUser));
      toast.success(`Bem-vindo, ${data.name}! Conectado via Google.`);
    } catch (err) {
      console.error('Error decoding Google JWT:', err);
      toast.error('Erro ao processar login com o Google.');
    }
  };

  // Inicializar o Google Identity Services
  useEffect(() => {
    const clientId =
      process.env.REACT_APP_GOOGLE_CLIENT_ID ||
      '730825509024-j7t1uiv7kbqjcjj47t6em89j2906tc5i.apps.googleusercontent.com';

    const initGoogle = () => {
      if (window.google?.accounts?.id && googleBtnRef.current) {
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
            auto_select: false,
          });

          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: 'filled_black',
            size: 'medium',
            shape: 'pill',
            text: 'signin_with',
            locale: 'pt-BR',
          });
        } catch (e) {
          console.warn('Google GSI render notice:', e);
        }
      }
    };

    initGoogle();
    const timer = setTimeout(initGoogle, 700);
    return () => clearTimeout(timer);
  }, [user]);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('nostalbeat_google_user');
    toast.info('Você saiu da sua conta Google.');
  };

  const handlePromptGoogle = () => {
    if (!user) {
      if (window.google?.accounts?.id?.prompt) {
        window.google.accounts.id.prompt();
      }
      toast.info('Por favor, faça login com sua conta do Google para comentar!');
    }
  };

  // Enviar comentário para o banco de dados via API
  const handleSendComment = async (e) => {
    if (e) e.preventDefault();

    if (!user || user.provider !== 'google') {
      handlePromptGoogle();
      return;
    }

    if (!message.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        google_id: user.id,
        googleId: user.id,
        user_name: user.name,
        userName: user.name,
        name: user.name,
        user_email: user.email,
        userEmail: user.email,
        user_picture: user.picture,
        userPicture: user.picture,
        message: message.trim(),
      };

      const response = await api.post('/comments', payload);
      const rawComment = response.data?.comment || response.data?.data;

      if (rawComment) {
        const normalized = normalizeComment(rawComment, user);
        setComments((prev) => [normalized, ...prev]);
        setMessage('');
        toast.success('Recado publicado no mural com sucesso!');

        // Rolar para o topo da lista
        if (commentsListRef.current) {
          commentsListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (err) {
      console.error('Erro ao enviar recado:', err);
      toast.error(err.response?.data?.error || 'Erro ao publicar recado.');
    } finally {
      setSubmitting(false);
    }
  };

  // Curtir / descurtir comentário
  const handleToggleLike = async (commentId) => {
    if (!commentId) return;
    const isCurrentlyLiked = !!likedMap[commentId];
    const nextLiked = !isCurrentlyLiked;
    const action = nextLiked ? 'like' : 'unlike';

    // Atualização otimista na tela
    setComments((prev) =>
      prev.map((c) => {
        const id = c.id || c._id;
        if (id === commentId) {
          const currentCount = c.likesCount ?? c.likes_count ?? c.likes ?? 0;
          const nextCount = nextLiked ? currentCount + 1 : Math.max(0, currentCount - 1);
          return {
            ...c,
            likesCount: nextCount,
            likes_count: nextCount,
          };
        }
        return c;
      })
    );

    const updated = { ...likedMap, [commentId]: nextLiked };
    setLikedMap(updated);
    try {
      localStorage.setItem('nostalbeat_liked_comments', JSON.stringify(updated));
    } catch (e) {}

    // Notificar o backend
    try {
      await api.post(`/comments/${commentId}/like`, { action });
    } catch (err) {
      console.warn('Erro ao atualizar curtida no servidor:', err);
    }
  };

  // Excluir comentário no banco de dados (autor)
  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;
    try {
      await api.delete(`/comments/${commentId}`, {
        data: { google_id: user?.id, googleId: user?.id },
      });
      setComments((prev) => prev.filter((c) => (c.id || c._id) !== commentId));
      toast.info('Recado removido.');
    } catch (err) {
      console.error('Erro ao excluir recado:', err);
      toast.error(err.response?.data?.error || 'Erro ao remover recado.');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, md: 3.5 },
          background: 'linear-gradient(160deg, rgba(22, 24, 36, 0.96) 0%, rgba(12, 13, 20, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1.5px solid rgba(66, 133, 244, 0.35)',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(66, 133, 244, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          p: { xs: 2, sm: 2.2 },
          '@media (max-height: 720px)': {
            p: 1.5,
          },
          position: 'relative',
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(66, 133, 244, 0.55)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.85), 0 0 35px rgba(66, 133, 244, 0.35)',
          },
        }}
      >
        {/* Barra superior de neon multicolorida estilo Google & Cyberpunk */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3.5px',
            background: 'linear-gradient(90deg, #FF6B35 0%, #EA4335 20%, #FBBC05 40%, #34A853 60%, #4285F4 80%, #00D4FF 100%)',
            boxShadow: '0 0 16px rgba(66, 133, 244, 0.8), 0 0 30px rgba(0, 212, 255, 0.5)',
          }}
        />

        {/* Cabeçalho da seção */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            pb: 1.5,
            mb: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {/* Título com ícone Google e contador */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(66, 133, 244, 0.25) 0%, rgba(0, 212, 255, 0.1) 100%)',
                border: '1.5px solid rgba(66, 133, 244, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 18px rgba(66, 133, 244, 0.45)',
                flexShrink: 0,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.08) rotate(6deg)',
                  boxShadow: '0 0 25px rgba(0, 212, 255, 0.7)',
                  borderColor: '#00D4FF',
                },
              }}
            >
              <GoogleIcon size={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: 'Orbitron, sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '0.92rem', sm: '1.02rem' },
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #E8EAED 40%, #8AB4F8 75%, #00D4FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.6px',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    filter: 'drop-shadow(0 0 10px rgba(66, 133, 244, 0.35))',
                  }}
                >
                  Mural de Recados
                </Typography>
                {comments.length > 0 && (
                  <Chip
                    icon={
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#34A853',
                          boxShadow: '0 0 8px #34A853',
                          ml: '6px !important',
                          animation: 'pulseRecado 2s infinite',
                          '@keyframes pulseRecado': {
                            '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                            '50%': { transform: 'scale(1.25)', opacity: 1, boxShadow: '0 0 12px #34A853' },
                            '100%': { transform: 'scale(0.95)', opacity: 0.8 },
                          },
                        }}
                      />
                    }
                    label={`${comments.length} ${comments.length === 1 ? 'recado' : 'recados'}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      fontFamily: 'Orbitron, sans-serif',
                      letterSpacing: '0.3px',
                      bgcolor: 'rgba(66, 133, 244, 0.16)',
                      color: '#8AB4F8',
                      border: '1px solid rgba(66, 133, 244, 0.45)',
                      boxShadow: '0 0 10px rgba(66, 133, 244, 0.2)',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#9AA0A6',
                  fontSize: '0.72rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user ? (
                  <>
                    <Box component="span" sx={{ color: '#8AB4F8', fontWeight: 600 }}>
                      Olá, {user.name.split(' ')[0]}
                    </Box>
                    <span>• Conectado via Google</span>
                  </>
                ) : (
                  'Deixe seu recado com sua conta Google'
                )}
              </Typography>
            </Box>
          </Box>

          {/* Usuário conectado ou botão de login do Google */}
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Tooltip title={`Conectado como ${user.email}`}>
                <Chip
                  avatar={
                    <Avatar
                      src={user.picture || undefined}
                      alt={user.name}
                      imgProps={{ referrerPolicy: 'no-referrer' }}
                      sx={{ width: '22px !important', height: '22px !important' }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                  }
                  label={user.name.split(' ')[0]}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(66, 133, 244, 0.16)',
                    color: '#8AB4F8',
                    border: '1px solid rgba(66, 133, 244, 0.4)',
                    fontWeight: 600,
                    fontSize: '0.76rem',
                    height: 28,
                  }}
                />
              </Tooltip>
              <Tooltip title="Sair da conta Google">
                <IconButton
                  size="small"
                  onClick={handleLogout}
                  sx={{
                    color: '#9AA0A6',
                    p: 0.6,
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    '&:hover': {
                      color: '#EA4335',
                      borderColor: 'rgba(234, 67, 53, 0.4)',
                      bgcolor: 'rgba(234, 67, 53, 0.1)',
                    },
                  }}
                >
                  <Logout sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ flexShrink: 0 }}>
              {/* Botão Oficial do Google Identity Services */}
              <Box ref={googleBtnRef} sx={{ minHeight: 36 }} />
            </Box>
          )}
        </Box>

        {/* Feed de Comentários com Rolagem Customizada */}
        <Box
          ref={commentsListRef}
          sx={{
            maxHeight: { xs: 200, sm: 220 },
            '@media (max-height: 720px)': {
              maxHeight: 125,
            },
            '@media (min-height: 721px) and (max-height: 820px)': {
              maxHeight: 160,
            },
            '@media (min-height: 821px)': {
              maxHeight: 220,
            },
            overflowY: 'auto',
            pr: 0.8,
            mb: 1.5,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.35)',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, #4285F4 0%, #00D4FF 100%)',
              borderRadius: '8px',
              boxShadow: '0 0 10px rgba(66, 133, 244, 0.6)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#00D4FF',
            },
          }}
        >
          {/* Loading State */}
          {loading && comments.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ color: '#4285F4' }} />
            </Box>
          )}

          {/* Estado Vazio: Mural limpo */}
          {!loading && comments.length === 0 && (
            <Fade in timeout={600}>
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: 3,
                  border: '1px dashed rgba(66, 133, 244, 0.25)',
                }}
              >
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(66, 133, 244, 0.25) 0%, rgba(0, 212, 255, 0.08) 70%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                    border: '1.5px solid rgba(66, 133, 244, 0.4)',
                    boxShadow: '0 0 25px rgba(66, 133, 244, 0.3)',
                  }}
                >
                  <Forum sx={{ color: '#8AB4F8', fontSize: 26 }} />
                </Box>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: '#E4E6EB',
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: '0.88rem',
                    letterSpacing: '0.5px',
                    mb: 0.5,
                  }}
                >
                  Mural de Recados NostalBeat
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: '#9AA0A6',
                    fontSize: '0.78rem',
                    lineHeight: 1.4,
                    maxWidth: 290,
                  }}
                >
                  {user
                    ? 'Seja o primeiro a deixar um recado no campo abaixo!'
                    : 'Faça login com sua conta Google acima para deixar um recado!'}
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Cards dos Comentários do Banco de Dados */}
          {comments.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
              {comments.map((comment) => {
                const commentId = comment.id || comment._id;
                const commentGoogleId = comment.googleId || comment.google_id || comment.userId;
                const commentUserName =
                  comment.userName ||
                  comment.user_name ||
                  comment.name ||
                  comment.author ||
                  'Ouvinte NostalBeat';
                const commentUserPicture =
                  comment.userPicture ||
                  comment.user_picture ||
                  comment.picture;
                const commentLikes =
                  comment.likesCount ??
                  comment.likes_count ??
                  comment.likes ??
                  0;
                const commentDate =
                  comment.createdAt ||
                  comment.created_at ||
                  comment.time;
                const isAuthor =
                  user &&
                  ((commentGoogleId && String(user.id) === String(commentGoogleId)) ||
                    (user.name && commentUserName && user.name.trim().toLowerCase() === commentUserName.trim().toLowerCase()));
                const isLiked = !!likedMap[commentId];

                return (
                  <Fade in key={commentId || Math.random()} timeout={400}>
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.055) 0%, rgba(20, 24, 38, 0.6) 100%)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(66, 133, 244, 0.2)',
                        borderRadius: '14px',
                        p: 1.5,
                        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: 'linear-gradient(180deg, #00D4FF 0%, #4285F4 60%, #8B5CF6 100%)',
                          boxShadow: '0 0 10px rgba(0, 212, 255, 0.6)',
                        },
                        '&:hover': {
                          borderColor: 'rgba(66, 133, 244, 0.55)',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(30, 36, 56, 0.75) 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 20px rgba(66, 133, 244, 0.25)',
                        },
                      }}
                    >
                      {/* Autor e Horário */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          <Avatar
                            src={commentUserPicture || undefined}
                            alt={commentUserName}
                            imgProps={{ referrerPolicy: 'no-referrer' }}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: '#4285F4',
                              fontSize: '0.84rem',
                              fontWeight: 800,
                              border: '1.5px solid rgba(66, 133, 244, 0.6)',
                              boxShadow: '0 0 8px rgba(66, 133, 244, 0.3)',
                              flexShrink: 0,
                            }}
                          >
                            {(commentUserName || 'O').charAt(0).toUpperCase()}
                          </Avatar>

                          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: '#FFF',
                                fontSize: '0.86rem',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {commentUserName}
                            </Typography>
                            <Tooltip title="Conta Google Verificada">
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.3,
                                  bgcolor: 'rgba(66, 133, 244, 0.14)',
                                  border: '1px solid rgba(66, 133, 244, 0.35)',
                                  borderRadius: '6px',
                                  px: 0.5,
                                  py: 0.1,
                                  flexShrink: 0,
                                }}
                              >
                                <GoogleIcon size={11} />
                                <Typography sx={{ fontSize: '0.62rem', color: '#8AB4F8', fontWeight: 700, lineHeight: 1 }}>
                                  Google
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Ações / Exclusão para o próprio autor */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#9AA0A6',
                              fontSize: '0.68rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.3,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <AccessTime sx={{ fontSize: 11 }} />
                            {formatTimeAgo(commentDate)}
                          </Typography>

                          {isAuthor && (
                            <Tooltip title="Excluir meu recado">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteComment(commentId)}
                                sx={{
                                  color: '#80868B',
                                  p: 0.3,
                                  '&:hover': { color: '#EA4335' },
                                }}
                              >
                                <DeleteOutline sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Conteúdo da Mensagem */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#E8EAED',
                          fontSize: '0.85rem',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          pl: 0.5,
                          mb: 0.8,
                        }}
                      >
                        {comment.message}
                      </Typography>

                      {/* Botão de Curtida */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
                        <Box
                          onClick={() => handleToggleLike(commentId)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            color: isLiked ? '#FF2E93' : '#9AA0A6',
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            px: 1.2,
                            py: 0.35,
                            borderRadius: '16px',
                            bgcolor: isLiked ? 'rgba(255, 46, 147, 0.16)' : 'rgba(255, 255, 255, 0.05)',
                            border: `1px solid ${isLiked ? 'rgba(255, 46, 147, 0.55)' : 'rgba(255, 255, 255, 0.1)'}`,
                            boxShadow: isLiked ? '0 0 14px rgba(255, 46, 147, 0.35)' : 'none',
                            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                            '&:hover': {
                              color: '#FF2E93',
                              bgcolor: 'rgba(255, 46, 147, 0.24)',
                              borderColor: 'rgba(255, 46, 147, 0.7)',
                              transform: 'scale(1.05)',
                            },
                            '&:active': {
                              transform: 'scale(0.95)',
                            },
                          }}
                        >
                          {isLiked ? (
                            <Favorite sx={{ fontSize: 14, color: '#FF2E93', filter: 'drop-shadow(0 0 6px rgba(255, 46, 147, 0.8))' }} />
                          ) : (
                            <FavoriteBorder sx={{ fontSize: 14 }} />
                          )}
                          <span>{commentLikes > 0 ? commentLikes : 'Curtir'}</span>
                        </Box>
                      </Box>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Barra de Digitação de Comentário */}
        <Box
          component="form"
          onSubmit={handleSendComment}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pt: 1,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {/* Avatar do Usuário */}
          <Box
            onClick={() => {
              if (!user) handlePromptGoogle();
            }}
            sx={{
              position: 'relative',
              cursor: user ? 'default' : 'pointer',
              flexShrink: 0,
            }}
          >
            <Avatar
              src={user?.picture || undefined}
              alt={user?.name}
              imgProps={{ referrerPolicy: 'no-referrer' }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: user ? '#4285F4' : 'rgba(255, 255, 255, 0.08)',
                border: user ? '1.5px solid #00D4FF' : '1px solid rgba(255, 255, 255, 0.2)',
                fontSize: '0.88rem',
                fontWeight: 700,
                boxShadow: user ? '0 0 12px rgba(0, 212, 255, 0.4)' : 'none',
              }}
            >
              {user ? user.name.charAt(0).toUpperCase() : <Person sx={{ fontSize: 18, color: '#9AA0A6' }} />}
            </Avatar>
          </Box>

          {/* Cápsula de entrada de texto */}
          <Box
            onClick={() => {
              if (!user) handlePromptGoogle();
            }}
            sx={{
              flex: 1,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(66, 133, 244, 0.25)',
              borderRadius: '24px',
              display: 'flex',
              alignItems: 'center',
              px: 1.6,
              py: 0.5,
              transition: 'all 0.25s ease',
              cursor: user ? 'text' : 'pointer',
              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.4)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(66, 133, 244, 0.5)',
              },
              '&:focus-within': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                borderColor: '#4285F4',
                boxShadow: '0 0 16px rgba(66, 133, 244, 0.35)',
              },
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!user || submitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
              placeholder={
                user
                  ? `Escreva seu recado como ${user.name.split(' ')[0]}...`
                  : 'Faça login com o Google para deixar um recado...'
              }
              InputProps={{
                disableUnderline: true,
                sx: {
                  color: '#FFF',
                  fontSize: '0.84rem',
                  fontFamily: 'Roboto, sans-serif',
                  cursor: user ? 'text' : 'pointer',
                  '& input::placeholder': {
                    color: '#9AA0A6',
                    opacity: 1,
                  },
                },
              }}
            />

            {/* Botão de Enviar */}
            <IconButton
              type="submit"
              size="small"
              disabled={!user || !message.trim() || submitting}
              sx={{
                color: message.trim() ? '#FFF' : 'rgba(255, 255, 255, 0.25)',
                p: 0.7,
                ml: 0.5,
                transition: 'all 0.25s ease',
                background: message.trim()
                  ? 'linear-gradient(135deg, #4285F4 0%, #00D4FF 100%)'
                  : 'transparent',
                boxShadow: message.trim() ? '0 0 14px rgba(66, 133, 244, 0.5)' : 'none',
                '&:hover': {
                  background: message.trim()
                    ? 'linear-gradient(135deg, #5A95F5 0%, #22DDFF 100%)'
                    : 'rgba(255, 255, 255, 0.08)',
                  transform: 'scale(1.08)',
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.7)',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={17} sx={{ color: '#FFF' }} />
              ) : (
                <Send sx={{ fontSize: 17 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default GoogleCommentsSection;
